import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, Icon, cn, type IconName } from "@sagot/ui";
import type { ReactNode } from "react";
import type { BookingMode } from "./availability-model";

interface BookingModeBannerProps {
  mode: BookingMode;
  /** Name of the connected calendar, when known. */
  calendarName: string | null;
  calendarConnected: boolean;
  bookingsHref: string;
  integrationsHref: string;
  onSetHours: () => void;
}

interface Look {
  icon: IconName;
  tone: string;
  iconTone: string;
}

const LOOK: Readonly<Record<BookingMode, Look>> = {
  calendar: { icon: "check-circle", tone: "bg-success-soft", iconTone: "text-success" },
  request: { icon: "info", tone: "bg-info-soft", iconTone: "text-info" },
  unconfigured: { icon: "alert-triangle", tone: "bg-warning-soft", iconTone: "text-warning" },
};

/** What visitors get right now (`booking_mode`): status = icon + words + tint, never tint alone. */
export function BookingModeBanner({ mode, calendarName, calendarConnected, bookingsHref, integrationsHref, onSetHours }: BookingModeBannerProps) {
  const { t } = useTranslation();
  const look = LOOK[mode];
  const P = `build.actions.availability.banner.${mode}`;
  let body: ReactNode = t(`${P}.body`);
  let action: ReactNode = null;
  if (mode === "calendar") body = calendarName ? t(`${P}.body`, { calendar: calendarName }) : t(`${P}.bodyNoName`);
  if (mode === "request") {
    action = (
      <Button asChild variant="secondary" size="sm">
        <AppLink href={calendarConnected ? bookingsHref : integrationsHref}>
          <Icon name={calendarConnected ? "calendar" : "plug"} />
          {t(calendarConnected ? `${P}.open` : `${P}.connect`)}
        </AppLink>
      </Button>
    );
    body = (
      <>
        {t(`${P}.body`)}
        {calendarConnected ? null : <> {t(`${P}.connectHint`)}</>}
      </>
    );
  }
  if (mode === "unconfigured") {
    action = (
      <Button size="sm" onClick={onSetHours}>
        <Icon name="clock" />
        {t(`${P}.cta`)}
      </Button>
    );
  }
  return (
    <div className={cn("flex flex-col gap-3 rounded-md p-3 sm:flex-row sm:items-center", look.tone)} data-testid="booking-mode-banner" data-mode={mode} role={mode === "unconfigured" ? "alert" : "status"}>
      <Icon name={look.icon} className={cn("size-5 shrink-0", look.iconTone)} />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold text-fg">{t(`${P}.title`)}</p>
        <p className="mt-0.5 text-xs text-fg-muted">{body}</p>
      </div>
      {action}
    </div>
  );
}
